import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileUp, Loader2, Users, XCircle } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { toast } from '@/stores/toast-store';
import { useAdminTenants } from '@/hooks/use-admin';
import { exportCustomerTemplate, parseCustomersFromFile, type CustomerImportRow } from '@/lib/customer-import-file';
import {
  useCustomerImportPreview,
  useCustomerImportChunk,
  type ImportPreviewResult,
} from '@/hooks/use-customer-import';

const CHUNK_SIZE = 200;

interface ParsedState {
  fileName: string;
  rows: CustomerImportRow[];
  skipped: number;
  errors: string[];
  warnings: string[];
}

interface ImportOutcome {
  inserted: number;
  updated: number;
  errors: string[];
}

/**
 * Super-admin customer migration import: move a merchant's customer base
 * (identity, credit account, reward points) from a previous POS system into
 * ours via a spreadsheet upload. Flow: pick tenant → download/fill template
 * → upload & client-validate → server dry-run preview → chunked commit.
 */
export function AdminCustomerImportPage() {
  const { data: tenantsResp } = useAdminTenants({ perPage: 200 });
  const tenants: any[] = tenantsResp?.data ?? [];

  const [tenantId, setTenantId] = useState('');
  const [parsed, setParsed] = useState<ParsedState | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0); // chunks completed
  const [outcome, setOutcome] = useState<ImportOutcome | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previewMutation = useCustomerImportPreview();
  const chunkMutation = useCustomerImportChunk();

  const tenantName = useMemo(() => tenants.find((t) => t.id === tenantId)?.name ?? '', [tenants, tenantId]);
  const totalChunks = parsed ? Math.ceil(parsed.rows.length / CHUNK_SIZE) : 0;

  const resetFile = () => {
    setParsed(null);
    setPreview(null);
    setOutcome(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    resetFile();
    try {
      const result = await parseCustomersFromFile(file);
      setParsed({ fileName: file.name, ...result });
      if (result.rows.length === 0 && result.errors.length === 0) {
        toast.error('No data rows found in the file');
      }
    } catch (err) {
      toast.error(`Could not read file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const runPreview = async () => {
    if (!parsed || !tenantId) return;
    setPreview(null);
    setOutcome(null);
    try {
      const resp = await previewMutation.mutateAsync({ tenantId, rows: parsed.rows });
      setPreview(resp.data);
    } catch (err: any) {
      const msg: string = err?.error?.message ?? err?.message ?? 'Preview failed';
      if (/not found/i.test(msg)) {
        // pos-backend 404s on these routes when CUSTOMER_ENGAGEMENT_ENABLED is off
        toast.error('Customer engagement is not enabled on this environment’s POS backend');
      } else {
        toast.error(msg);
      }
    }
  };

  const runImport = async () => {
    if (!parsed || !tenantId) return;
    setConfirming(false);
    setImporting(true);
    setProgress(0);
    const totals: ImportOutcome = { inserted: 0, updated: 0, errors: [] };
    try {
      for (let i = 0; i < parsed.rows.length; i += CHUNK_SIZE) {
        const chunk = parsed.rows.slice(i, i + CHUNK_SIZE);
        const resp = await chunkMutation.mutateAsync({ tenantId, rows: chunk, rowIndexOffset: i });
        totals.inserted += resp.data.inserted;
        totals.updated += resp.data.updated;
        totals.errors.push(...resp.data.errors);
        setProgress(Math.floor(i / CHUNK_SIZE) + 1);
      }
      setOutcome(totals);
      if (totals.errors.length === 0) {
        toast.success(`Imported ${totals.inserted + totals.updated} customers for ${tenantName}`);
      } else {
        toast.error(`Import finished with ${totals.errors.length} row error(s)`);
      }
    } catch (err: any) {
      // A failed chunk stops the loop; completed chunks are already committed.
      setOutcome(totals);
      const msg: string = err?.error?.message ?? err?.message ?? 'request failed';
      toast.error(
        `Import stopped after ${totals.inserted + totals.updated} rows: ${msg}. ` +
          'Fix the issue and re-import the same file — already-imported rows will simply update.',
      );
    } finally {
      setImporting(false);
    }
  };

  const exportErrorsCsv = () => {
    if (!outcome) return;
    const lines = ['error', ...outcome.errors.map((e) => `"${e.replace(/"/g, '""')}"`)];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'customer_import_errors.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const canPreview = !!tenantId && !!parsed && parsed.rows.length > 0 && !importing;
  const canImport = canPreview && !!preview && preview.errors === 0 && preview.total > 0 && !previewMutation.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Import"
        description="Migrate a merchant's customers — with credit accounts and reward points — from their previous POS system."
      />

      {/* Step 1: tenant + template */}
      <div className="rounded-xl border border-border bg-white p-6 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-foreground">1. Choose merchant &amp; template</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="tenant-select" className="text-sm font-medium text-foreground">
              Merchant (tenant)
            </label>
            <select
              id="tenant-select"
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setPreview(null);
                setOutcome(null);
              }}
              disabled={importing}
              className="h-10 min-w-64 rounded-lg border border-border bg-white px-3 text-sm"
            >
              <option value="">Select a merchant…</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={exportCustomerTemplate}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            <Download className="h-4 w-4" />
            Download template
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          Fill the template from the old system&apos;s export. <code>credit_balance_owed</code> is the amount the
          customer owes (credit used). Rows whose phone matches an existing customer update that customer.
        </p>
      </div>

      {/* Step 2: upload */}
      <div className="rounded-xl border border-border bg-white p-6 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-foreground">2. Upload filled template</h2>
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90">
            <FileUp className="h-4 w-4" />
            {parsed ? 'Choose a different file' : 'Choose file'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              disabled={importing}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </label>
          {parsed && (
            <span className="text-sm text-muted-foreground">
              {parsed.fileName} — {parsed.rows.length} valid row{parsed.rows.length === 1 ? '' : 's'}
              {parsed.skipped > 0 && `, ${parsed.skipped} skipped`}
            </span>
          )}
        </div>

        {parsed && parsed.errors.length > 0 && (
          <IssueList
            tone="error"
            title={`${parsed.errors.length} row(s) have problems and will NOT be imported`}
            items={parsed.errors}
          />
        )}
        {parsed && parsed.warnings.length > 0 && (
          <IssueList tone="warning" title={`${parsed.warnings.length} warning(s)`} items={parsed.warnings} />
        )}

        <button
          type="button"
          onClick={runPreview}
          disabled={!canPreview || previewMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {previewMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Preview import
        </button>
        {!tenantId && parsed && <p className="text-sm text-amber-600">Select a merchant to preview.</p>}
      </div>

      {/* Step 3: preview + commit */}
      {preview && (
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-foreground">3. Review &amp; import</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Rows" value={preview.total} />
            <StatCard label="New customers" value={preview.inserts} tone="success" />
            <StatCard label="Updates" value={preview.updates} tone="info" />
            <StatCard label="Errors" value={preview.errors} tone={preview.errors > 0 ? 'error' : undefined} />
          </div>

          {preview.rows.some((r) => r.action === 'error' || r.warnings.length > 0) && (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Row</th>
                    <th className="px-3 py-2 font-medium">Phone</th>
                    <th className="px-3 py-2 font-medium">Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows
                    .filter((r) => r.action === 'error' || r.warnings.length > 0)
                    .map((r) => (
                      <tr key={r.row} className="border-t border-border">
                        <td className="px-3 py-2">{r.row}</td>
                        <td className="px-3 py-2">{r.phone}</td>
                        <td className="px-3 py-2">
                          {r.errors.length > 0 ? (
                            <span className="text-red-600">{r.errors.join('; ')}</span>
                          ) : (
                            <span className="text-amber-600">{r.warnings.join('; ')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.errors > 0 ? (
            <p className="text-sm text-red-600">
              Fix the rows above in the spreadsheet and re-upload — the import is blocked while any row has errors.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!canImport || importing}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {importing && <Loader2 className="h-4 w-4 animate-spin" />}
              {importing
                ? `Importing… chunk ${progress}/${totalChunks}`
                : `Import ${preview.total} customers into ${tenantName || 'merchant'}`}
            </button>
          )}

          {importing && (
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${totalChunks ? Math.round((progress / totalChunks) * 100) : 0}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Step 4: results */}
      {outcome && (
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Results</h2>
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <span className="inline-flex items-center gap-1.5 text-green-700">
              <CheckCircle2 className="h-4 w-4" /> {outcome.inserted} created
            </span>
            <span className="inline-flex items-center gap-1.5 text-blue-700">
              <Users className="h-4 w-4" /> {outcome.updated} updated
            </span>
            <span
              className={`inline-flex items-center gap-1.5 ${outcome.errors.length > 0 ? 'text-red-600' : 'text-muted-foreground'}`}
            >
              <XCircle className="h-4 w-4" /> {outcome.errors.length} errors
            </span>
          </div>
          {outcome.errors.length > 0 && (
            <>
              <IssueList tone="error" title="Row errors" items={outcome.errors} />
              <button
                type="button"
                onClick={exportErrorsCsv}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                <Download className="h-4 w-4" />
                Export errors as CSV
              </button>
            </>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Import customers?</h3>
            <p className="text-sm text-muted-foreground">
              This will create <strong>{preview?.inserts ?? 0}</strong> and update{' '}
              <strong>{preview?.updates ?? 0}</strong> customers for{' '}
              <strong>{tenantName || 'the selected merchant'}</strong>, including credit balances and reward points.
              Re-running the same file later is safe.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runImport}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                Yes, import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'info' | 'error' }) {
  const valueColor =
    tone === 'success'
      ? 'text-green-700'
      : tone === 'info'
        ? 'text-blue-700'
        : tone === 'error'
          ? 'text-red-600'
          : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`text-2xl font-semibold ${valueColor}`}>{value}</p>
    </div>
  );
}

function IssueList({ tone, title, items }: { tone: 'error' | 'warning'; title: string; items: string[] }) {
  const isError = tone === 'error';
  return (
    <div className={`rounded-lg border p-4 ${isError ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
      <p
        className={`mb-2 inline-flex items-center gap-1.5 text-sm font-medium ${isError ? 'text-red-700' : 'text-amber-700'}`}
      >
        <AlertTriangle className="h-4 w-4" />
        {title}
      </p>
      <ul className={`max-h-40 space-y-1 overflow-y-auto text-sm ${isError ? 'text-red-700' : 'text-amber-700'}`}>
        {items.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  );
}
